// datoteka: src/Pages/Admin/ManageBlog.js

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Uvozite API funkcije, ki ste jih definirali v koraku 1
import * as adminApi from '../../Services/adminApi';

const ManageBlog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [currentPost, setCurrentPost] = useState(null); // null for new, object for edit
    const [isSaving, setIsSaving] = useState(false); // Za prikaz nalaganja na gumbu "Save"

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await adminApi.getBlogPosts();
            setPosts(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch posts.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const handleEdit = (post) => {
        setCurrentPost(post);
        setShowModal(true);
    };

    const handleCreate = () => {
        // Pripravimo prazen objekt za nov zapis
        setCurrentPost({
            title: '',
            content: '',
            status: 'draft',
            meta_description: '',
            meta_keywords: ''
        });
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setCurrentPost(null);
    };

    const handleSave = async () => {
        if (!currentPost || !currentPost.title) {
            alert('Title is required.');
            return;
        }
        setIsSaving(true);
        setError('');

        try {
            if (currentPost.id) {
                // Posodabljamo obstoječ zapis
                await adminApi.updateBlogPost(currentPost.id, currentPost);
            } else {
                // Ustvarjamo nov zapis
                await adminApi.createBlogPost(currentPost);
            }
            handleClose();
            fetchPosts(); // Osvežimo seznam po uspešnem shranjevanju
        } catch (err) {
            setError(err.message || 'Failed to save the post.');
            // Če je prišlo do napake, modal ostane odprt, da lahko uporabnik popravi vnos
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Ali ste prepričani, da želite izbrisati ta zapis? Dejanje je trajno.")) {
            try {
                await adminApi.deleteBlogPost(id);
                fetchPosts(); // Osvežimo seznam
            } catch (err) {
                alert(err.message || 'Failed to delete the post.');
            }
        }
    };

    if (loading) {
        return <div className="text-center"><Spinner animation="border" /></div>;
    }

    return (
        <div>
            <h2>Upravljanje z Blogom</h2>
            <Button onClick={handleCreate} className="mb-3">
                <i className="fas fa-plus me-2"></i> Ustvari nov zapis
            </Button>

            {error && !showModal && <Alert variant="danger">{error}</Alert>}

            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Naslov</th>
                        <th>Status</th>
                        <th>Datum objave</th>
                        <th>Akcije</th>
                    </tr>
                </thead>
                <tbody>
                    {posts.length > 0 ? posts.map(post => (
                        <tr key={post.id}>
                            <td>{post.title}</td>
                            <td>
                                <Badge bg={post.status === 'published' ? 'success' : 'secondary'}>
                                    {post.status}
                                </Badge>
                            </td>
                            <td>{new Date(post.created_at).toLocaleDateString()}</td>
                            <td>
                                <Button variant="info" size="sm" onClick={() => handleEdit(post)} className="me-2">
                                    <i className="fas fa-edit"></i>
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDelete(post.id)}>
                                    <i className="fas fa-trash"></i>
                                </Button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan="4" className="text-center">Ni najdenih zapisov.</td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <Modal show={showModal} onHide={handleClose} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{currentPost?.id ? 'Uredi zapis' : 'Ustvari nov zapis'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && showModal && <Alert variant="danger">{error}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Naslov</Form.Label>
                            <Form.Control
                                type="text"
                                value={currentPost?.title || ''}
                                onChange={(e) => setCurrentPost({ ...currentPost, title: e.target.value })}
                            />
                        </Form.Group>
                        {/* DODANO: Polje za Meta Opis */}
                        <Form.Group className="mb-3">
                            <Form.Label>Meta Opis (Meta Description)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Kratek opis vsebine za iskalnike (cca. 160 znakov)"
                                value={currentPost?.meta_description || ''}
                                onChange={(e) => setCurrentPost({ ...currentPost, meta_description: e.target.value })}
                            />
                        </Form.Group>

                        {/* DODANO: Polje za Meta Ključne besede */}
                        <Form.Group className="mb-3">
                            <Form.Label>Meta Ključne besede (Meta Keywords)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="npr. rabljena oblačila, nasveti, moda"
                                value={currentPost?.meta_keywords || ''}
                                onChange={(e) => setCurrentPost({ ...currentPost, meta_keywords: e.target.value })}
                            />
                            <Form.Text className="text-muted">
                                Ključne besede ločite z vejico (,).
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Vsebina</Form.Label>
                            <ReactQuill
                                theme="snow"
                                value={currentPost?.content || ''}
                                onChange={(content) => setCurrentPost({ ...currentPost, content })}
                                style={{ height: '250px', marginBottom: '50px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={currentPost?.status || 'draft'}
                                onChange={(e) => setCurrentPost({ ...currentPost, status: e.target.value })}
                            >
                                <option value="draft">Osnutek (Draft)</option>
                                <option value="published">Objavljeno (Published)</option>
                            </Form.Select>
                        </Form.Group>
                        {/* TODO: Po potrebi dodajte polja za meta opise in slike */}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isSaving}>Zapri</Button>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Shranjevanje...</> : 'Shrani spremembe'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ManageBlog;